import { InfiniteListBase, ShowBase, useShowContext, useListContext } from "ra-core";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { Task } from "./Task";
import { AddTask } from "./AddTask";
import type { Contact } from "../types";

const getTaskStatus = (task: { due_date: string; done_date: string | null }) => {
  if (task.done_date) return { label: "已完成", color: "bg-green-500", text: "text-green-600" };
  if (new Date(task.due_date) < new Date()) return { label: "进行中", color: "bg-orange-500", text: "text-orange-600" };
  return { label: "即将开始", color: "bg-blue-500", text: "text-blue-600" };
};

const AllTasksList = () => {
  const { data, isPending, error } = useListContext();
  if (isPending || error) return null;
  if (!data || data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">暂无任务</p>
    );
  }
  return (
    <div>
      {data.map((task, index) => {
        const status = getTaskStatus(task);
        return (
          <div key={task.id}>
            <div className="py-2">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${status.color}`} />
                <span className={`text-xs font-medium ${status.text}`}>{status.label}</span>
              </div>
              <Task task={task} />
            </div>
            {index < data.length - 1 && <Separator />}
          </div>
        );
      })}
    </div>
  );
};

const ContactTasksPageContent = () => {
  const { record, isPending } = useShowContext<Contact>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  if (isPending || !record) return null;

  return (
    <div className="mt-2 mb-2">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/contacts/${id}/show`)}
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold">
            {record.first_name} {record.last_name}
          </h2>
          <p className="text-sm text-muted-foreground">全部任务</p>
        </div>
        <AddTask />
      </div>
      <Card>
        <CardContent>
          <InfiniteListBase
            resource="tasks"
            filter={{ contact_id: record.id }}
            sort={{ field: "due_date", order: "ASC" }}
            perPage={50}
            disableSyncWithLocation
            storeKey={false}
          >
            <AllTasksList />
          </InfiniteListBase>
        </CardContent>
      </Card>
    </div>
  );
};

export const ContactTasksPage = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <ShowBase resource="contacts" id={id}>
      <ContactTasksPageContent />
    </ShowBase>
  );
};
